import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { signInWithGoogle } from "@/lib/oauth";
import { toast } from "sonner";
import { Home, Mail, Search } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import Seo from "@/components/Seo";
import { useTenant } from "@/hooks/useTenant";
import { getIntent, setIntent, clearIntent } from "@/lib/onboardingIntent";
import { resolvePostAuthRoute } from "@/lib/postAuthRoute";
import { hasDraftListing } from "@/lib/draftListing";
import { markTermsPending } from "@/lib/termsAcceptance";
import { updateWelcomePrefs } from "@/lib/welcomePrefs";

// /login is SIGN-IN. Account creation lives in the two role flows, the
// renter preference wizard (/welcome) and the owner onboarding
// (/owner-onboarding), so "Sign up" here only asks which one you are and
// hands you off. The old role-blind email+password signup form is gone.

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get("redirect");
  const modeParam = searchParams.get("mode");
  const emailParam = searchParams.get("email") ?? "";
  const { user, loading: authLoading } = useAuth();
  const tenant = useTenant();

  // Routes a logged-in user to the right place based on redirect + intent +
  // profile role. The resolver owns the priority order (explicit redirect
  // first, so claim-listing links and "contact this owner" round-trips work
  // for brand-new accounts too; /onboarding only as the final fallback).
  const routeAfterAuth = async (userId: string) => {
    // maybeSingle so a missing profile row (or 500) doesn't throw, we still
    // want to route the user sensibly even if their profile didn't materialize.
    const { data, error } = await supabase
      .from("profiles")
      .select("display_name, user_types")
      .eq("user_id", userId)
      .maybeSingle();

    if (error) {
      toast.error("Couldn't load your profile, taking you to onboarding.");
      navigate("/onboarding", { replace: true });
      return;
    }

    let types = (data?.user_types as string[]) ?? [];
    const intent = getIntent();
    const draft = hasDraftListing();

    // If the user arrived with intent (e.g. clicked "List your home") and
    // doesn't yet have a role on file, persist it now so we never lose
    // track of who they are. Then we can stop relying on the localStorage
    // intent on subsequent sessions.
    if (types.length === 0 && intent) {
      const { error } = await supabase
        .from("profiles")
        .update({ user_types: [intent] })
        .eq("user_id", userId);
      if (!error) {
        types = [intent];
        clearIntent();
      }
    }

    const dest = resolvePostAuthRoute({
      types,
      intent,
      redirect: redirectParam,
      hasDraft: draft,
    });
    navigate(dest, { replace: true });
  };

  useEffect(() => {
    if (!authLoading && user) {
      routeAfterAuth(user.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  // Sign-in is the default; ?mode=signup (role chooser) is opt-in, every
  // "Sign In" button in the app now opens the tab its label promises.
  const [showChooser, setShowChooser] = useState(modeParam === "signup");
  const [email, setEmail] = useState(emailParam);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  // After a failed sign-in we surface a one-tap "email me a link to set a
  // password" recovery path. This is the migration route for legacy users who
  // created their account with the old 4-digit PIN: their PIN no longer works
  // at sign-in, so we make resetting frictionless instead of flagging or
  // emailing everyone up front.
  const [recoveryState, setRecoveryState] = useState<"idle" | "offered" | "sent">("idle");
  const [failedPinLike, setFailedPinLike] = useState(false);

  const withRedirect = (path: string, param: "redirect" | "next") =>
    redirectParam ? `${path}?${param}=${encodeURIComponent(redirectParam)}` : path;

  const chooseRenter = () => {
    setIntent("renter");
    // Seed the wizard with a known email (e.g. from the newsletter nudge link).
    if (emailParam) updateWelcomePrefs({ email: emailParam });
    navigate(withRedirect("/welcome", "redirect"));
  };

  const chooseOwner = () => {
    setIntent("owner");
    navigate(withRedirect("/owner-onboarding", "next"));
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast.error("Enter your email above first, then click Forgot password.");
      return;
    }
    setForgotLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setRecoveryState("sent");
      toast.success("Check your email for a password reset link.");
    } catch (err: any) {
      toast.error(err.message ?? "Couldn't send reset email");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      markTermsPending();
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      toast.success("Welcome back!");
      if (data.user) {
        await routeAfterAuth(data.user.id);
      }
    } catch (err: any) {
      toast.error(err.message);
      // A 4-digit attempt is almost certainly a legacy PIN user, so we tailor
      // the recovery copy.
      setFailedPinLike(/^\d{4}$/.test(password));
      setRecoveryState("offered");
    } finally {
      setLoading(false);
    }
  };

  // Google on the sign-in screen: existing users come straight back; a
  // brand-new Google account falls through routeAfterAuth to the /onboarding
  // role wizard (users who *know* they're signing up take the chooser above).
  const handleGoogle = async () => {
    markTermsPending();
    const { error } = await signInWithGoogle({ redirectTo: window.location.href });
    if (error) toast.error("Google sign-in failed");
  };

  return (
    <div className="min-h-[100svh] flex items-start justify-center">
      <Seo
        title={`Sign in or create account | ${tenant.siteName}`}
        description={`Sign in to ${tenant.siteName} to find rentals, favorite homes, and contact owners.`}
        path="/login"
        noindex
      />
      <div className="w-full max-w-sm mx-auto px-4 py-16">
        {showChooser ? (
          <>
            <h1 className="font-serif text-3xl font-semibold text-center mb-2">Welcome Home</h1>
            <p className="text-muted-foreground text-center text-sm mb-8">
              What brings you to {tenant.siteName}?
            </p>

            <div className="space-y-3">
              <button
                type="button"
                onClick={chooseRenter}
                className="w-full flex items-start gap-3 border rounded-xl px-4 py-4 text-left transition-colors hover:border-primary/60 hover:bg-primary/5"
              >
                <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <Search className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold">I'm looking for a place to rent</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Tell us what you need: we'll match homes, save favorites, and connect you with owners.
                  </p>
                </div>
              </button>
              <button
                type="button"
                onClick={chooseOwner}
                className="w-full flex items-start gap-3 border rounded-xl px-4 py-4 text-left transition-colors hover:border-primary/60 hover:bg-primary/5"
              >
                <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <Home className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold">I have a place to rent out</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    List your home, get quality renter leads, keep conversations in one place.
                  </p>
                </div>
              </button>
            </div>

            <p className="text-center text-sm text-muted-foreground mt-8">
              Already have an account?{" "}
              <button
                onClick={() => setShowChooser(false)}
                className="text-primary font-medium hover:underline"
              >
                Sign in
              </button>
            </p>
          </>
        ) : (
          <>
            <h1 className="font-serif text-3xl font-semibold text-center mb-2">Welcome Back</h1>
            <p className="text-muted-foreground text-center text-sm mb-8">
              Sign in to access your saved properties
            </p>

            <Button
              variant="outline"
              className="w-full rounded-full gap-2 mb-6"
              onClick={handleGoogle}
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              Continue with Google
            </Button>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">or</span>
              </div>
            </div>

            <form onSubmit={handleSignIn} className="space-y-3">
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="Email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setRecoveryState("idle"); }}
                required
                className="w-full bg-background border rounded-input px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              />
              <input
                type="password"
                autoComplete="current-password"
                placeholder="Password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); if (recoveryState === "offered") setRecoveryState("idle"); }}
                required
                minLength={6}
                className="w-full bg-background border rounded-input px-4 py-3 text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              />

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={forgotLoading}
                  className="text-xs text-muted-foreground hover:text-primary hover:underline disabled:opacity-50"
                >
                  {forgotLoading ? "Sending..." : "Forgot password?"}
                </button>
              </div>

              <Button type="submit" className="w-full rounded-full gap-2" disabled={loading}>
                <Mail className="h-4 w-4" />
                {loading ? "Please wait..." : "Sign In"}
              </Button>
            </form>

            <p className="text-center text-xs text-muted-foreground mt-4 px-2">
              By continuing, you agree to our{" "}
              <Link to="/terms" className="underline hover:text-foreground">Terms</Link>
              {" "}and{" "}
              <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>.
            </p>

            {recoveryState !== "idle" && (
              <div className="mt-5 rounded-xl border border-primary/30 bg-primary/5 p-4">
                {recoveryState === "sent" ? (
                  <p className="text-sm text-foreground">
                    ✓ Check your email: we sent a link to{" "}
                    <span className="font-medium break-all">{email}</span> to set a
                    new password.
                  </p>
                ) : (
                  <>
                    <p className="text-sm font-medium text-foreground mb-1">
                      Can't sign in?
                    </p>
                    <p className="text-sm text-muted-foreground mb-3">
                      {failedPinLike
                        ? "It looks like you used a 4-digit PIN. Sign-in has been upgraded. We'll email you a secure link to set a password."
                        : "We'll email you a secure link to set a new password."}
                    </p>
                    <Button
                      type="button"
                      onClick={handleForgotPassword}
                      disabled={forgotLoading}
                      className="w-full rounded-full gap-2"
                    >
                      <Mail className="h-4 w-4" />
                      {forgotLoading ? "Sending..." : "Email me a secure link"}
                    </Button>
                  </>
                )}
              </div>
            )}

            <p className="text-center text-sm text-muted-foreground mt-6">
              Don't have an account?{" "}
              <button
                onClick={() => { setShowChooser(true); setRecoveryState("idle"); }}
                className="text-primary font-medium hover:underline"
              >
                Sign up
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
